/**
 * EBO-ALERTS offline capture store for VHF signal reports.
 * Kept in a dedicated IndexedDB so it can be evolved independently of
 * the PHC consultation store. Records drain via vhfSignalSync.ts.
 */
const DB_NAME = 'ebo-alerts-db';
const DB_VERSION = 1;
const STORE = 'vhf_signals';

export type VhfSignalGroup =
  | 'severe_fever_vhf'
  | 'unusual_deaths'
  | 'hcw_illness'
  | 'lab_referral_delay'
  | 'ambulance_delay'
  | 'cross_border';

export type VhfSignalSource = 'facility' | 'community' | 'lab' | 'referral' | 'border' | 'other';
export type VhfChannel = 'app' | 'sms' | 'paper' | 'phone' | 'email';

export interface OfflineVhfSignal {
  localId: string;
  signalGroup: VhfSignalGroup;
  source: VhfSignalSource;
  sourceChannel: VhfChannel;
  facilityId: string | null;
  organizationId: string | null;
  village: string | null;
  healthZone: string | null;
  districtCode: string | null;
  countryCode: string; // ISO-2, uppercase, e.g. UG / CD
  gpsLat: number | null;
  gpsLng: number | null;
  onsetAt: string | null;
  reportedAt: string;
  denominator: number | null;
  notes: string | null;
  reportedBy: string | null;
  createdAt: string;
  synced: boolean;
  syncedAt: string | null;
  remoteId: string | null;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: 'localId' });
        s.createIndex('synced', 'synced', { unique: false });
        s.createIndex('countryCode', 'countryCode', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const r = fn(t.objectStore(STORE));
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
        t.oncomplete = () => db.close();
      }),
  );
}

export const vhfSignalOfflineDb = {
  async save(signal: OfflineVhfSignal): Promise<void> {
    await tx('readwrite', (s) => s.put(signal));
  },
  async getUnsynced(): Promise<OfflineVhfSignal[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const t = db.transaction(STORE, 'readonly');
      const r = t.objectStore(STORE).getAll();
      r.onsuccess = () =>
        resolve(((r.result || []) as OfflineVhfSignal[]).filter((s) => !s.synced));
      r.onerror = () => reject(r.error);
      t.oncomplete = () => db.close();
    });
  },
  async markSynced(localId: string, remoteId: string): Promise<void> {
    const existing = await tx<OfflineVhfSignal>('readonly', (s) => s.get(localId));
    if (!existing) return;
    existing.synced = true;
    existing.syncedAt = new Date().toISOString();
    existing.remoteId = remoteId;
    await tx('readwrite', (s) => s.put(existing));
  },
  async getUnsyncedCount(): Promise<number> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const t = db.transaction(STORE, 'readonly');
      const r = t.objectStore(STORE).getAll();
      r.onsuccess = () => resolve(((r.result || []).filter((r: any) => !r.synced)).length);
      r.onerror = () => reject(r.error);
      t.oncomplete = () => db.close();
    });
  },
};

export function newVhfSignalLocalId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `vhf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
